import { AuthMeCohort } from '@/utils/auth-me';
import type { AuthTenant } from '@/types/tenant';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AuthMethod = 'password' | 'sso';

interface AuthState {
  isAuthenticated: boolean;
  authMethod: AuthMethod | null;
  orgId: string | null;
  tenants: AuthTenant[] | null;
  showTenantSelector: boolean;
  currentCohorts: AuthMeCohort[];
  setSession: (authMethod: AuthMethod) => void;
  setOrgId: (orgId: string | null) => void;
  setCurrentCohorts: (cohorts: AuthMeCohort[]) => void;
  setAuthParams: (params: {
    isAuthenticated?: boolean;
    authMethod?: AuthMethod | null;
  }) => void;
  clearAuth: () => void;
  setTenants: (tenants: AuthTenant[]) => void;
  setShowTenantSelector: (show: boolean) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      authMethod: null,
      orgId: null,
      tenants: null,
      showTenantSelector: false,
      currentCohorts: [],
      setCurrentCohorts: (currentCohorts) => set({ currentCohorts }),
      setSession: (authMethod) =>
        set({ isAuthenticated: true, authMethod }),
      setAuthParams: (params) => set({ ...params }),
      setTenants: (tenants) => set({ tenants }),
      setOrgId: (orgId) => set({ orgId }),
      setShowTenantSelector: (show) => set({ showTenantSelector: show }),
      clearAuth: () =>
        set({
          isAuthenticated: false,
          authMethod: null,
          orgId: null,
          tenants: null,
          currentCohorts: [],
        }),
      reset: () =>
        set({
          isAuthenticated: false,
          authMethod: null,
          orgId: null,
          tenants: null,
          showTenantSelector: false,
          currentCohorts: [],
        }),
    }),
    {
      name: 'auth-storage',
      version: 3,
      migrate: (persistedState, version) => {
        const state = {
          ...(persistedState as Record<string, unknown>),
        };
        delete state.token;
        delete state.idToken;

        if (version < 3) {
          delete state.tenantId;
          delete state.tenantSlug;
          delete state.currentTenant;
        }

        return state as unknown as AuthState;
      },
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        authMethod: state.authMethod,
        orgId: state.orgId,
        tenants: state.tenants,
        showTenantSelector: state.showTenantSelector,
        currentCohorts: state.currentCohorts,
      }),
    }
  )
);
